import { z } from "zod";

/**
 * Revisión técnica independiente (Fase 3). Contrato del informe que devuelve el revisor y vista que expone la API.
 *
 * El informe es un DATO NO CONFIABLE hasta validarlo: el backend comprueba el formato y que cada hallazgo apunte a un fichero y
 * a una línea del diff revisado. Un informe bien formado no demuestra que un hallazgo sea cierto: las sospechas se marcan como
 * tales (`confidence: "suspected"`) y «sin hallazgos» no es una garantía de ausencia de defectos.
 */

export const REVIEW_SEVERITIES = ["critical", "high", "medium", "low"] as const;
export type ReviewSeverity = (typeof REVIEW_SEVERITIES)[number];
/** Solo lo que interesa a la entrega. No hay categoría de estilo ni de refactorización opcional: no son motivo de hallazgo. */
export const REVIEW_CATEGORIES = ["behavior", "requirement", "security", "missing_test"] as const;
export type ReviewCategory = (typeof REVIEW_CATEGORIES)[number];
export const REVIEW_CONFIDENCE = ["confirmed", "suspected"] as const;

/** Modo de revisión por repositorio. Solo existe el modo sombra: registra y muestra, no bloquea ni cambia aprobaciones. */
export const REVIEW_MODES = ["off", "shadow"] as const;
export type ReviewMode = (typeof REVIEW_MODES)[number];

/** Estado persistido de una revisión. `stale` = el SHA de la PR cambió antes de lanzarla (no cuenta como ejecución). */
export const REVIEW_STATUSES = ["skipped", "pending", "running", "completed", "failed", "stale", "exhausted"] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

/** Estado que se muestra para la entrega ACTUAL (el SHA de la cabeza de la PR). */
export const REVIEW_VIEW_STATES = ["disabled", "not_selected", "skipped", "pending", "running", "completed", "failed", "outdated", "exhausted"] as const;
export type ReviewViewState = (typeof REVIEW_VIEW_STATES)[number];

export const reviewFindingSchema = z.object({
  file: z.string().min(1).max(500),
  line: z.number().int().positive(),
  severity: z.enum(REVIEW_SEVERITIES),
  category: z.enum(REVIEW_CATEGORIES),
  confidence: z.enum(REVIEW_CONFIDENCE),
  title: z.string().min(1).max(200),
  evidence: z.string().min(1).max(2000),
  effect: z.string().min(1).max(1000),
  rationale: z.string().min(1).max(2000),
});
export type ReviewFinding = z.infer<typeof reviewFindingSchema>;

/** Salida del revisor ANTES de validar las referencias al diff (los hallazgos se validan uno a uno). */
export const reviewReportSchema = z.object({
  outcome: z.enum(["findings", "no_findings"]),
  summary: z.string().max(2000),
  coverage: z.object({
    reviewedFiles: z.array(z.string().max(500)).max(500),
    notReviewed: z.array(z.object({ path: z.string().max(500), reason: z.string().max(500) })).max(500),
  }),
  findings: z.array(z.unknown()).max(50),
  uncertainties: z.array(z.string().max(1000)).max(30),
});

/** El mismo contrato en JSON Schema, para la salida estructurada del proveedor. La validación de verdad es la de Zod en el backend. */
export const REVIEW_REPORT_JSON_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["outcome", "summary", "coverage", "findings", "uncertainties"],
  properties: {
    outcome: { type: "string", enum: ["findings", "no_findings"] },
    summary: { type: "string" },
    coverage: {
      type: "object",
      additionalProperties: false,
      required: ["reviewedFiles", "notReviewed"],
      properties: {
        reviewedFiles: { type: "array", items: { type: "string" } },
        notReviewed: { type: "array", items: { type: "object", additionalProperties: false, required: ["path", "reason"], properties: { path: { type: "string" }, reason: { type: "string" } } } },
      },
    },
    findings: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["file", "line", "severity", "category", "confidence", "title", "evidence", "effect", "rationale"],
        properties: {
          file: { type: "string" },
          line: { type: "integer" },
          severity: { type: "string", enum: [...REVIEW_SEVERITIES] },
          category: { type: "string", enum: [...REVIEW_CATEGORIES] },
          confidence: { type: "string", enum: [...REVIEW_CONFIDENCE] },
          title: { type: "string" },
          evidence: { type: "string" },
          effect: { type: "string" },
          rationale: { type: "string" },
        },
      },
    },
    uncertainties: { type: "array", items: { type: "string" } },
  },
} as const;

/** Consumo de una ejecución del revisor: medido (lo informó el proveedor) o desconocido (nunca un 0 inventado). */
export interface ReviewUsageDTO {
  state: "measured" | "unknown";
  input: number | null;
  output: number | null;
  cacheRead: number | null;
  cacheWrite: number | null;
  total: number | null;
  source: string | null;
}

export interface ReviewFindingDTO extends ReviewFinding {
  /** En un modo activo (no implementado) bloquearía: severidad alta o crítica, confirmado y de comportamiento, requisito o seguridad. */
  wouldBlock: boolean;
}

export interface DeliveryReviewDTO {
  id: string;
  headSha: string;
  status: ReviewStatus;
  trigger: "auto" | "human_retry";
  mode: ReviewMode;
  /** Por qué se revisa o se omite (reglas deterministas sobre riesgo, clase de cambio y perfil). */
  decision: { action: "review" | "skip"; code: string; reason: string; rulesVersion: string };
  executionNo: number | null;
  authorization: { kind: "initial" | "correction" | "human_retry"; detail: string } | null;
  provider: string | null;
  model: string | null;
  instructionsVersion: string | null;
  outcome: "findings" | "no_findings" | null;
  summary: string | null;
  findings: ReviewFindingDTO[];
  rejectedFindings: { reason: string; finding: unknown }[];
  uncertainties: string[];
  coverage: { reviewedFiles: string[]; notReviewed: { path: string; reason: string }[]; complete: boolean } | null;
  /** Motivo de omisión, fallo, desactualización o agotamiento. */
  statusReason: { code: string; detail: string } | null;
  usage: ReviewUsageDTO | null;
  execution: { stopReason: string | null; requestId: string | null; durationMs: number | null } | null;
  /** El SHA de la PR cambió después: el informe es histórico y no valida la entrega actual. */
  superseded: { at: string; bySha: string | null } | null;
  createdAt: string;
  launchedAt: string | null;
  finishedAt: string | null;
}

export interface MissionReviewDTO {
  enabled: boolean;
  repoMode: ReviewMode;
  providerConfigured: boolean;
  currentHeadSha: string | null;
  state: ReviewViewState;
  stateDetail: string;
  /** Revisión que corresponde al SHA actual (la más reciente), si existe. */
  current: DeliveryReviewDTO | null;
  rounds: { executionsUsed: number; executionsMax: number; correctionsAuthorized: number; correctionsMax: number };
  canRetry: boolean;
  retryBlockedReason: string | null;
  history: DeliveryReviewDTO[];
}
